import * as React from 'react';
import { ArrowRight } from 'lucide-react';
import { Badge, Box, SimpleGrid } from '@chakra-ui/react';
import {
  Heading,
  HStack,
  SeverityBadge,
  SeverityDisplay,
  SeverityLevel,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { Badge as LocalBadge } from 'components/ui/badge';
import { Chip } from 'components/ui/chip';
import { Chip as PackageChip } from '@ayble-health/dashboard-components';
import { Callout } from './Callout';
import {
  TYPE_USAGE_MEASURED_ON,
  UNUSED_FONT_SIZES,
  UNUSED_FONT_WEIGHTS,
} from './typeScaleUsage';
import {
  fontSizeTokens,
  fontWeightTokens,
  lineHeightTokens,
} from '../../theme/chakra';
import { Specimen } from './Specimen';
import { legacyPalettes } from '../../theme/chakra';
import { MeetingStatusBadge } from '../UserDashboard/MeetingStatusBadge';
import { PmStatus } from '../../api/types';

// Called out in TOKENS.md as "NB defaults, rarely used — audit before adding
// new usages".
const FLAGGED_LEGACY_FAMILIES = ['coolGray', 'trueGray', 'amber', 'warmGray'];

const LegacyPalettes = () => (
  <VStack gap={4} align="stretch">
    {Object.entries(legacyPalettes).map(([family, shades]) => {
      const flagged = FLAGGED_LEGACY_FAMILIES.includes(family);
      return (
        <VStack key={family} gap={1} align="stretch">
          <HStack gap={2} align="center">
            <Text fontWeight="semibold" color="muted.900">
              {family}
            </Text>
            {flagged && (
              <Text fontSize="xs" color="orange" fontWeight="bold">
                — NB default, rarely used; audit before adding usages
              </Text>
            )}
          </HStack>
          <HStack gap={2} align="stretch" wrap="wrap">
            {Object.keys(shades).map((shade) => (
              <VStack key={shade} gap={1} minW="72px">
                <Box
                  bg={`${family}.${shade}`}
                  h="40px"
                  w="72px"
                  rounded="md"
                  borderWidth="1px"
                  borderColor={flagged ? 'orange' : 'cloud'}
                />
                <Text fontSize="2xs" color="muted.400">
                  {shade}
                </Text>
              </VStack>
            ))}
          </HStack>
        </VStack>
      );
    })}
  </VStack>
);

const BADGE_PALETTES = [
  'primary',
  'secondary',
  'info',
  'success',
  'warning',
  'danger',
] as const;
const BADGE_VARIANTS = ['solid', 'subtle', 'outline', 'surface'] as const;

/**
 * The two square careOS Tag variants, retired once Badge took over square
 * geometry. Kept here because an archived variant needs somewhere to be looked
 * up: someone will find `variant="feather-sm"` in an old branch or a Figma
 * frame and need to know where it went.
 */

const LINE_HEIGHT_SAMPLE =
  'Track allergies, sensitivities, and preferences across every visit.';

/**
 * Steps of the shared type scale that nothing in careOS uses.
 *
 * Still live in the theme — they belong to the package, and this repo pulls
 * rather than edits. They are here so the Typography section can show only the
 * steps that are real choices, without the wider scale disappearing entirely.
 */

/**
 * The one package Chip variant careOS does not offer.
 *
 * Rendered from the package directly, because the local Chip no longer types
 * it — which is the point of archiving it rather than deleting the note.
 */
const OmittedChipOutline = () => (
  <VStack gap={4} align="stretch">
    <Callout title="Chip outline — omitted in favour of symptom">
      Both are transparent pills; they differ only in border strength.{' '}
      <code>outline</code> uses a full-strength evergreen <code>#043338</code>{' '}
      border with evergreen text; <code>symptom</code> uses a lighter bluegrey{' '}
      <code>#718F9A</code> border with seafoam text, which is the weight these
      labels want against careOS surfaces. Offering both means choosing between
      two nearly identical pills every time, so careOS offers one. Nothing was
      using <code>outline</code>, so no call site changed.
      <br />
      <br />
      Still exported by the package and still shown in full on the Repo tab —
      this narrows the careOS vocabulary, it does not change upstream.
    </Callout>

    <Specimen
      name="Chip outline → Chip symptom"
      note="Left: the package variant, no longer offered locally. Right: what careOS uses instead."
      flagged
    >
      <HStack gap={6} align="center" wrap="wrap">
        <VStack gap={1} align="flex-start">
          <PackageChip variant="outline">
            outline
          </PackageChip>
          <Text my={0} fontSize="2xs" fontFamily="mono" color="muted.400">
            evergreen #043338 border + text
          </Text>
        </VStack>
        <VStack gap={1} align="flex-start">
          <Chip variant="symptom">symptom</Chip>
          <Text my={0} fontSize="2xs" fontFamily="mono" color="muted.400">
            bluegrey #718F9A border · seafoam #2D6670 text
          </Text>
        </VStack>
      </HStack>
    </Specimen>
  </VStack>
);

const UnusedTypeScale = () => (
  <VStack gap={4} align="stretch">
    <Callout title="Type scale — steps careOS does not use">
      The scale comes from <code>@ayble-health/dashboard-components/theme</code>{' '}
      and carries Chakra&rsquo;s full range. Usage counted across{' '}
      <code>src/</code> on {TYPE_USAGE_MEASURED_ON}, excluding the design system
      itself — which enumerates every token, so counting it would make
      everything look used. These are still valid tokens; they are just not
      choices anyone is making today.
    </Callout>

    <Specimen
      name="Font sizes — unused"
      note="48px and up. The largest thing on any careOS screen is the 36px greeting."
      flagged
    >
      <VStack gap={2} align="stretch" w="100%">
        {UNUSED_FONT_SIZES.map((token) => (
          <HStack key={token} gap={4} align="baseline">
            <Text
              my={0}
              minW="56px"
              fontSize="2xs"
              fontFamily="mono"
              color="muted.400"
            >
              {token}
            </Text>
            <Text my={0} fontSize="2xs" fontFamily="mono" color="muted.400">
              {fontSizeTokens[token]?.value}
            </Text>
          </HStack>
        ))}
      </VStack>
    </Specimen>

    <Specimen
      name="Font weights — unused"
      note="CircularXX ships no cuts at these weights, so a browser would synthesise them."
      flagged
    >
      <VStack gap={2} align="stretch" w="100%">
        {UNUSED_FONT_WEIGHTS.map((token) => (
          <HStack key={token} gap={4} align="baseline">
            <Text
              my={0}
              minW="90px"
              fontSize="2xs"
              fontFamily="mono"
              color="muted.400"
            >
              {token}
            </Text>
            <Text my={0} fontSize="2xs" fontFamily="mono" color="muted.400">
              {fontWeightTokens[token]?.value}
            </Text>
          </HStack>
        ))}
      </VStack>
    </Specimen>

    <Specimen
      name="Line heights — the whole group is unused"
      note="Nothing sets lineHeight to any of these. The shared Text defaults to `base` and Heading to `shorter` — neither name is in this token set, so both resolve against Chakra's own defaults instead. careOS surfaces set line height through careos-prototype.css."
      flagged
    >
      <VStack gap={2} align="stretch" w="100%">
        {Object.entries(lineHeightTokens).map(([token, { value }]) => (
          <HStack key={token} gap={4} align="baseline">
            <Text
              my={0}
              minW="56px"
              fontSize="2xs"
              fontFamily="mono"
              color="muted.400"
            >
              {token}
            </Text>
            <Text my={0} fontSize="2xs" fontFamily="mono" color="muted.400">
              {value}
            </Text>
            <Text my={0} lineHeight={token} fontSize="xs" color="muted.600">
              {LINE_HEIGHT_SAMPLE}
            </Text>
          </HStack>
        ))}
      </VStack>
    </Specimen>
  </VStack>
);

/**
 * The retired recipe, as data.
 *
 * `ui/tag.tsx` is gone, so this cannot render a real `Tag` any more — and a
 * page documenting a deleted component must not be the one thing keeping it
 * alive. The fills are transcribed from the recipe as it stood at deletion so
 * the row still shows what each variant looked like.
 *
 * Written as brand tokens rather than hex: every colour Tag used is still in
 * the palette, so this stays correct if a token is retuned, and a reader can
 * see that nothing here was bespoke. `border: null` means the variant had none.
 */
const RETIRED_TAG_VARIANTS = [
  ['feather', 'feather', 'seafoam', null, 'Chip neutral — exact colour match'],
  ['seafoam', 'seafoam', 'white', null, 'Chip positive — exact colour match'],
  ['fuschia', 'fuschia', 'white', null, 'Chip negative — exact colour match'],
  ['mint', 'mint', 'seafoam', 'sage', 'Chip mint — v1.3.0'],
  ['ballet', 'ballet', 'plum', 'peppa', 'Chip ballet — v1.3.0'],
  ['aqua', 'aqua', 'white', null, 'Chip aqua — v1.3.0'],
  ['canvas', 'canvas', 'seafoam', 'cloud', 'Chip canvas — v1.3.0'],
  ['symptom', 'transparent', 'bluegrey', 'bluegrey', 'Chip symptom — v1.3.0'],
] as const;

/**
 * A dead swatch, not a component. Deliberately a plain `Box` with the recipe's
 * own base values inlined — no import, nothing anyone can reach for by
 * accident, and no screenshot to go stale or ignore the reader's theme.
 */
const RetiredTagSwatch = ({
  bg,
  fg,
  border,
  children,
}: {
  bg: string;
  fg: string;
  border: string | null;
  children: React.ReactNode;
}) => (
  <Box
    display="inline-flex"
    alignItems="center"
    bg={bg}
    color={fg}
    borderRadius="pill"
    borderWidth={border ? '1px' : 0}
    borderStyle="solid"
    borderColor={border ?? undefined}
    fontFamily="body"
    fontSize="xs"
    fontWeight="semibold"
    lineHeight="sm"
    letterSpacing="0.01em"
    padding="4px 8px"
    whiteSpace="nowrap"
  >
    {children}
  </Box>
);

/**
 * careOS `Tag`, retired in favour of `Chip`.
 *
 * Kept renderable because the recipe still exists, but nothing outside this
 * audit uses it any more. It is here rather than in Atoms so nobody reaches
 * for it in new work.
 */
const RetiredTag = () => (
  <VStack gap={4} align="stretch">
    <Callout title="careOS Tag — superseded by Chip">
      Tag and Chip were two names for the same object: a compact pill label. The
      audit showed five Tag variants matching a Chip or Badge variant on colour
      exactly, and the rest were addable. Every Tag variant now exists as a
      Chip, so Tag has no reason to stay in Atoms.
      <br />
      <br />
      <strong>Gone from feature code.</strong> The last two call sites were the
      Care roster&rsquo;s Cancelled and New enrollment badges (
      <code>HealthCoachDashboard</code>); both are Chips now, and the migrated
      badges were measured against the Tag rendering afterwards and match it to
      the pixel.
      <br />
      <br />
      <strong>The code is gone.</strong> <code>ui/tag.tsx</code> was deleted once
      the last three consumers moved off it: <code>PillToggleGroup</code> now
      composes <code>PressablePill</code>, and this audit&rsquo;s own{' '}
      <code>ChangeBadge</code> and <code>ParityBadge</code> are Chips. The
      swatches below are inert markup with the recipe&rsquo;s values transcribed
      onto brand tokens — a record of what each variant looked like, not a
      component anyone can import. Nothing in the repo can render a{' '}
      <code>Tag</code> any more.
    </Callout>

    <Specimen
      name="Tag — every variant, and where it went"
      note="Two square variants (feather-sm, outline) were archived separately below and moved to Badge."
      flagged
    >
      <VStack gap={3} align="stretch" w="100%">
        {RETIRED_TAG_VARIANTS.map(([variant, bg, fg, border, target]) => (
          <HStack key={variant} gap={3} align="center" wrap="wrap">
            <Box minW="90px">
              <RetiredTagSwatch bg={bg} fg={fg} border={border}>
                Label
              </RetiredTagSwatch>
            </Box>
            <Text my={0} fontSize="2xs" fontFamily="mono" color="muted.400">
              {variant}
            </Text>
            <Text my={0} fontSize="xs" color="muted.600">
              → {target}
            </Text>
          </HStack>
        ))}
      </VStack>
    </Specimen>
  </VStack>
);

const ArchivedSquareTags = () => (
  <VStack gap={4} align="stretch">
    <Callout title="Square Tag variants — moved to Badge">
      careOS <code>Tag</code> is a pill set: every variant is a 100px radius.
      Two variants were square (4px) — <code>feather-sm</code> and{' '}
      <code>outline</code> — which made them the odd members of the set and
      duplicated geometry <code>Badge</code> already had. Both moved to Badge
      with their colours intact, and now take Badge&rsquo;s metrics (0 6px
      padding, weight 500) instead of Tag&rsquo;s. They are gone from the Tag
      recipe, so the old names no longer compile. UPSTREAM_PROPOSALS.md carries
      the full colour-by-colour comparison.
    </Callout>

    <Specimen
      name='Tag variant="feather-sm" → Badge variant="neutral"'
      note="feather #EEF4F6 fill, seafoam #2D6670 text. Was: 4px 8px padding, weight 600. Now: Badge metrics."
      flagged
    >
      <HStack gap={3} align="center">
        <LocalBadge variant="neutral">Label</LocalBadge>
        <Text my={0} fontSize="xs" color="muted.400">
          call site: LabsTab · removable test tags
        </Text>
      </HStack>
    </Specimen>

    <Specimen
      name='Tag variant="outline" → Badge variant="outline"'
      note="transparent fill, seafoam #2D6670 text, bluegrey #718F9A border, weight 400 — the light weight is deliberate and was carried over."
      flagged
    >
      <HStack gap={3} align="center">
        <LocalBadge variant="outline">E11.9</LocalBadge>
        <Text my={0} fontSize="xs" color="muted.400">
          call site: PatientProfile · billing priorities / diagnosis codes
        </Text>
      </HStack>
    </Specimen>
  </VStack>
);

const DeprecatedBadges = () => (
  <VStack gap={4} align="stretch">
    <Callout title="Superseded by StatusPill">
      StatusPill (Patterns → Status) is the single source of truth for status
      badges. The raw Chakra Badge palettes below were never adopted by feature
      code (call sites set inline colors instead), and MeetingStatusBadge is the
      old patient-profile pill. Both are kept for reference; migrate usages to
      StatusPill.
    </Callout>

    <Specimen name="Chakra Badge — variant × palette" flagged>
      <VStack gap={4} align="stretch" w="100%">
        {BADGE_VARIANTS.map((variant) => (
          <HStack key={variant} gap={3} align="center" wrap="wrap">
            <Text
              minW="72px"
              fontSize="2xs"
              color="muted.400"
              fontFamily="mono"
            >
              {variant}
            </Text>
            {BADGE_PALETTES.map((palette) => (
              <Badge key={palette} variant={variant} colorPalette={palette}>
                {palette}
              </Badge>
            ))}
          </HStack>
        ))}
      </VStack>
    </Specimen>

    <Specimen name="MeetingStatusBadge — patient profile (deprecated)" flagged>
      <HStack gap={3} wrap="wrap" align="center">
        <MeetingStatusBadge isConfirmed pmStatus={undefined} />
        <MeetingStatusBadge isConfirmed={false} pmStatus={undefined} />
        <MeetingStatusBadge isConfirmed pmStatus={PmStatus.cancelled} />
        <MeetingStatusBadge isConfirmed pmStatus={PmStatus.lateCancelled} />
        <MeetingStatusBadge isConfirmed pmStatus={PmStatus.noShow} />
      </HStack>
    </Specimen>
  </VStack>
);

// original off-token hex → aligned non-legacy brand token (applied in code)
const ALIGNED: Array<{
  name: string;
  was: string;
  token: string;
  hex: string;
  note?: string;
}> = [
  {
    name: 'INK',
    was: '#20262A',
    token: 'evergreen',
    hex: '#043338',
    note: 'no neutral near-black in palette',
  },
  { name: 'SUBTLE', was: '#8B9298', token: 'bluegrey', hex: '#718F9A' },
  {
    name: 'SUBTLE_STRONG',
    was: '#5C646A',
    token: 'seafoam',
    hex: '#2D6670',
    note: 'no neutral mid-gray in palette',
  },
  {
    name: 'TEAL',
    was: '#5299A9',
    token: 'aqua',
    hex: '#5299A9',
    note: 'exact match',
  },
  { name: 'PLUM', was: '#873A5C', token: 'fuschia', hex: '#B34575' },
  { name: 'ACTIVE_BG', was: '#E7F0F1', token: 'mint', hex: '#DEF2F2' },
  { name: 'ACTIVE_BORDER', was: '#BBD9DC', token: 'steel', hex: '#BCC7D7' },
  {
    name: 'ACTIVE_BAR',
    was: '#2D6670',
    token: 'seafoam',
    hex: '#2D6670',
    note: 'exact match',
  },
  { name: 'CARD_BORDER', was: '#ECECEC', token: 'cloud', hex: '#E7E7E7' },
  {
    name: 'no-show (MeetingStatusBadge)',
    was: '#5D1505',
    token: 'tomato',
    hex: '#CD3A3A',
    note: 'nearest red',
  },
];

const Swatch = ({ hex }: { hex: string }) => (
  <Box
    bg={hex}
    h="32px"
    w="48px"
    rounded="md"
    borderWidth="1px"
    borderColor="cloud"
    flexShrink={0}
  />
);

const SeverityLabels = () => (
  <VStack gap={4} align="stretch">
    <Callout title="Severity labels — archived">
      The gradient severity bars (SeverityBadge / SeverityLevel /
      SeverityDisplay) are legacy food/symptom indicators kept for reference.
      They use a bespoke color ramp outside the semantic palettes; migrate to a
      token-based indicator before reusing.
    </Callout>
    <SimpleGrid columns={{ base: 1, md: 3 }} gap={3}>
      <Specimen name="SeverityBadge (levels 0–3)" flagged>
        <VStack gap={2} align="flex-start">
          {[0, 1, 2, 3].map((level) => (
            <SeverityBadge key={level} level={level} />
          ))}
        </VStack>
      </Specimen>
      <Specimen name="SeverityLevel" flagged>
        <SeverityLevel level={3} />
      </Specimen>
      <Specimen name="SeverityDisplay" flagged>
        <SeverityDisplay label="Apple" level={2} />
      </Specimen>
    </SimpleGrid>
  </VStack>
);

const AlignedColors = () => (
  <VStack gap={4} align="stretch">
    <Callout title="Off-token colors — aligned to brand tokens (done)">
      ProviderHome's tokens.ts and MeetingStatusBadge no longer carry private
      hex — each was pinned to its nearest non-legacy brand token (below). Two
      gaps remain in the brand palette that forced imperfect matches: there's no
      neutral near-black (INK → evergreen reads teal) and no neutral mid-gray
      (SUBTLE_STRONG → seafoam). Worth adding a neutral ramp to the theme.
    </Callout>

    <VStack gap={2} align="stretch">
      {ALIGNED.map((a) => (
        <HStack
          key={a.name}
          gap={4}
          align="center"
          wrap="wrap"
          borderWidth="1px"
          borderColor="cloud"
          rounded="md"
          px={3}
          py={2}
        >
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color="muted.900"
            minW="200px"
            fontFamily="mono"
          >
            {a.name}
          </Text>
          <HStack gap={2} align="center">
            <Swatch hex={a.was} />
            <Text fontSize="2xs" color="muted.400" fontFamily="mono">
              {a.was}
            </Text>
          </HStack>
          <ArrowRight size={14} color="#8B9298" />
          <HStack gap={2} align="center">
            <Swatch hex={a.hex} />
            <Text fontSize="xs" color="muted.900">
              {a.token}
            </Text>
            <Text fontSize="2xs" color="muted.400" fontFamily="mono">
              {a.hex}
            </Text>
          </HStack>
          {a.note && (
            <Text fontSize="2xs" color="orange" fontWeight="bold">
              {a.note}
            </Text>
          )}
        </HStack>
      ))}
    </VStack>
  </VStack>
);

/**
 * Archive tab: legacy and deprecated patterns kept for reference and
 * migration. Everything here is something Eng should clean up or migrate
 * off — separated from the current system so the canonical set stays clean.
 */
export const Archive = () => (
  <VStack gap={12} align="stretch">
    <VStack gap={1} align="stretch">
      <Text maxW="760px">
        Legacy and deprecated patterns, separated from the current system so
        what's canonical stays unambiguous. Everything here is a cleanup or
        migration target — not something to build against.
      </Text>
    </VStack>

    <VStack gap={3} align="stretch">
      <Heading size="lg">Legacy tier palettes</Heading>
      <Text maxW="720px">
        Tier-named shades inherited from native-base (primary.500, muted.600,
        …). New code should use brand names + semantic palettes; these remain
        only for the migrated codebase.
      </Text>
      <LegacyPalettes />
    </VStack>

    <VStack gap={3} align="stretch">
      <Heading size="lg">Deprecated badges</Heading>
      <OmittedChipOutline />

      <UnusedTypeScale />

      <RetiredTag />

      <ArchivedSquareTags />

      <DeprecatedBadges />
    </VStack>

    <VStack gap={3} align="stretch">
      <Heading size="lg">Severity labels</Heading>
      <SeverityLabels />
    </VStack>

    <VStack gap={3} align="stretch">
      <Heading size="lg">Aligned off-token colors</Heading>
      <AlignedColors />
    </VStack>
  </VStack>
);
